import type { Category } from '~~/types/commercetools';
import type { CategoryTree } from '~~/types/category/CategoryTree';
import { buildCategoryPageUrl } from '~~/helpers/category/buildCategoryPageUrl';
import { findCategoryTreeByKey, removeEmptyCategories } from '~~/helpers/category/categoriesTree';
import type { ExtendedCategory, RelatedCategory, RelatedTree } from '~~/types/category/RelatedCategory';
import { RELATED_CATEGORIES_KEYS } from '~~/constants/relatedCategory';
import type { CountryCodeValue } from '~~/types/localization/localizationHelperTypes';

type LocaleOptions = {
  languageAndCountry: string;
  countryCode: CountryCodeValue;
}

const adjustRelatedCategories = ({
  categories,
  countryCode,
  categoryTree,
}: {
  categories: Category[];
  countryCode: CountryCodeValue;
  categoryTree?: CategoryTree;
}): RelatedCategory[] => {

  return categories?.map((category) => ({
    name: category.name || '',
    url: buildCategoryPageUrl({
      category: category.slug || '',
      countryCode,
      categoryTree,
    }),
  })) ?? [];

};

const getRelatedTree = (
  rawCategories: Category[],
  localOptions: LocaleOptions,
): Maybe<RelatedTree> => {

  const filteredRawCategories = removeEmptyCategories(rawCategories, localOptions.languageAndCountry);

  if (!filteredRawCategories?.length) {

    return null;
  
  }

  const rootCategory = filteredRawCategories?.[0]?.ancestors[0];

  return {
    title: rootCategory?.name || '',
    categories: adjustRelatedCategories({
      categories: filteredRawCategories,
      countryCode: localOptions.countryCode,
      categoryTree: findCategoryTreeByKey(rootCategory?.key),
    }),
  };

};

export const getRelatedTrees = (category: ExtendedCategory, localOptions: LocaleOptions): RelatedTree[] => {

  const relatedTrees = [];
  for (const key of RELATED_CATEGORIES_KEYS) {

    const rawCategories = category?.custom?.[key]?.[0]?.referencedResourceSet;

    if (!rawCategories?.length) {

      continue;
    
    }

    const relatedTree = getRelatedTree(rawCategories, localOptions);

    if (relatedTree) {

      relatedTrees.push(relatedTree);
    
    }
  
  }

  return relatedTrees;

};
